import React from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import * as R from 'ramda'

import { useControlledForm, useFormApiError } from 'hooks'
import { mapInternalStateToPayload } from './actions'
import { TEMPLATE_NAME_FIELD, getDefaultValues } from './model'
import { getSchema } from './schema'

const useFormState = (state, handlers) => {
  const [apiError, setApiError] = React.useState(null)

  const { templateName } = state
  const { onConfirm } = handlers

  const defaultValues = getDefaultValues()

  const schema = getSchema()

  const handleFormData = React.useCallback(
    async data => {
      const payload = mapInternalStateToPayload({ ...data })

      if (R.equals(templateName, R.view(R.lensProp('title'), payload))) return

      await onConfirm(payload, { onError: setApiError })
    },
    [templateName, onConfirm],
  )

  const create = useControlledForm({
    defaultValues: defaultValues,
    resolver: yupResolver(schema),
    values: { [TEMPLATE_NAME_FIELD]: templateName },
    handleFormData,
  })

  useFormApiError({ apiError }, create.handleFormApiError)

  return { create }
}

export const useInternalState = (state, handlers) => {
  const { templateName } = state
  const { onCancel, onClose, onConfirm } = handlers

  const { create } = useFormState({ templateName }, { onConfirm })

  const handleCancel = React.useCallback(() => {
    onCancel()
    create.handleFormReset()
  }, [create, onCancel])

  const handleClose = React.useCallback(() => {
    onClose()
    create.handleFormReset()
  }, [create, onClose])

  const handleConfirm = React.useCallback(() => {
    create.handleFormSubmit()
  }, [create])

  return {
    state: { create },
    handlers: { handleCancel, handleClose, handleConfirm },
  }
}
